import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/EmptyState";
import DocumentList from "@/components/documents/DocumentList";
import DocumentUploadDialog from "@/components/documents/DocumentUploadDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VISIBILITY_OPTIONS } from "@/lib/documents";
import { Plus, Search, FolderOpen, Filter } from "lucide-react";

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [search, setSearch] = useState("");
  const [visibility, setVisibility] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/documents");
    setDocs(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = (docs || []).filter((d) => {
    if (visibility !== "all" && d.visibility !== visibility) return false;
    if (search) {
      const s = search.toLowerCase();
      return (d.original_filename || "").toLowerCase().includes(s)
        || (d.category || "").toLowerCase().includes(s)
        || (d.case_title || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div data-testid="documents-page">
      <PageHeader
        title="Documents"
        subtitle="Securely store and share case files with your clients."
        actions={
          <Button onClick={() => setUploadOpen(true)} data-testid="upload-doc-btn" className="bg-gold text-navy hover:bg-gold-dark">
            <Plus className="mr-1.5 h-4 w-4" /> Upload Document
          </Button>
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="documents-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, category or case…" className="pl-9" />
        </div>
        <Select value={visibility} onValueChange={setVisibility}>
          <SelectTrigger className="w-[180px]" data-testid="documents-visibility-filter"><Filter className="mr-1 h-3.5 w-3.5" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Visibilities</SelectItem>
            {VISIBILITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!docs ? <LoadingState /> : docs.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No documents yet" description="Upload pleadings, orders and evidence, and share them securely with clients." testid="documents-empty"
          action={<Button onClick={() => setUploadOpen(true)} className="bg-navy text-white hover:bg-navy-light"><Plus className="mr-1.5 h-4 w-4" /> Upload Document</Button>} />
      ) : (
        <DocumentList documents={filtered} onChanged={load} showCase />
      )}

      <DocumentUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
    </div>
  );
}
